<template>
  <div class="recommend-container">
    <div class="fixed-header">
      <h2>推荐管理</h2>
      <el-button type="primary" :disabled="loading || calculating" @click="runCalculation">
        <el-icon v-if="calculating" class="is-loading"><Loading /></el-icon>
        <span v-if="calculating">计算中...</span>
        <span v-else>运行相关性计算</span>
      </el-button>
    </div>

    <!-- 统计信息 -->
    <div class="stats-section">
      <el-card>
        <template #header>
          <div class="card-header">
            <span>统计信息</span>
            <el-button text @click="refreshStats">
              <el-icon><Refresh /></el-icon>
              刷新
            </el-button>
          </div>
        </template>
        <div class="stats-content" v-loading="loading">
          <div class="stat-item">
            <div class="stat-label">在线游戏数量</div>
            <div class="stat-value">{{ stats.onlineGamesCount || 0 }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">已计算相关性游戏数</div>
            <div class="stat-value">{{ stats.relateGamesCount || 0 }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">覆盖率</div>
            <div class="stat-value">{{ stats.coverage || 0 }}%</div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 配置选项 -->
    <div class="config-section">
      <el-card>
        <template #header>
          <span>计算配置</span>
        </template>
        <el-form :model="config" label-width="180px">
          <el-form-item label="相似度阈值">
            <el-input-number
              v-model="config.similarity_threshold"
              :min="0"
              :max="1"
              :step="0.01"
              :precision="2"
            />
            <div class="form-item-tip">只保留相似度大于此值的游戏（0-1之间）</div>
          </el-form-item>

          <el-form-item label="最大相关游戏数">
            <el-input-number
              v-model="config.max_related_games"
              :min="1"
              :max="50"
            />
            <div class="form-item-tip">每个游戏保留的相关游戏数量</div>
          </el-form-item>

          <el-divider />

          <el-form-item label="使用字段">
            <el-checkbox v-model="config.use_desc">描述 (desc)</el-checkbox>
            <el-checkbox v-model="config.use_tags">标签 (tags)</el-checkbox>
            <el-checkbox v-model="config.use_categories">分类 (categories)</el-checkbox>
          </el-form-item>

          <el-form-item label="字段权重">
            <div style="display: flex; flex-direction: column; gap: 12px;">
              <div style="display: flex; align-items: center; gap: 12px;">
                <span style="width: 100px;">描述权重:</span>
                <el-slider
                  v-model="config.desc_weight"
                  :min="0"
                  :max="5"
                  :step="0.1"
                  :precision="1"
                  style="flex: 1;"
                />
                <span style="width: 60px;">{{ config.desc_weight }}</span>
              </div>
              <div style="display: flex; align-items: center; gap: 12px;">
                <span style="width: 100px;">标签权重:</span>
                <el-slider
                  v-model="config.tags_weight"
                  :min="0"
                  :max="5"
                  :step="0.1"
                  :precision="1"
                  style="flex: 1;"
                />
                <span style="width: 60px;">{{ config.tags_weight }}</span>
              </div>
              <div style="display: flex; align-items: center; gap: 12px;">
                <span style="width: 100px;">分类权重:</span>
                <el-slider
                  v-model="config.categories_weight"
                  :min="0"
                  :max="5"
                  :step="0.1"
                  :precision="1"
                  style="flex: 1;"
                />
                <span style="width: 60px;">{{ config.categories_weight }}</span>
              </div>
            </div>
          </el-form-item>

          <el-divider />

          <el-form-item label="高级选项">
            <div style="display: flex; flex-direction: column; gap: 12px;">
              <el-checkbox v-model="config.enable_diversity">启用多样性优化</el-checkbox>
              <div v-if="config.enable_diversity" style="display: flex; align-items: center; gap: 12px; margin-left: 24px;">
                <span style="width: 120px;">多样性阈值:</span>
                <el-slider
                  v-model="config.diversity_threshold"
                  :min="0"
                  :max="1"
                  :step="0.05"
                  :precision="2"
                  style="flex: 1;"
                />
                <span style="width: 60px;">{{ config.diversity_threshold }}</span>
                <div class="form-item-tip">相似度超过此值的游戏会被过滤</div>
              </div>
              <el-checkbox v-model="config.enable_business_rules">启用业务规则增强</el-checkbox>
              <div class="form-item-tip" style="margin-left: 24px;">
                业务规则包括：热门游戏优先、新游戏优先、高评分优先等
              </div>
            </div>
          </el-form-item>
        </el-form>
      </el-card>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { Loading, Refresh } from '@element-plus/icons-vue';
import { RecommendApi, RecommendStats, RecommendConfig } from '../../api/recommendApi';

export default defineComponent({
  name: 'Recommend',
  components: {
    Loading,
    Refresh,
  },
  setup() {
    const loading = ref(false);
    const calculating = ref(false);
    const stats = ref<RecommendStats>({
      relateGamesCount: 0,
      onlineGamesCount: 0,
      coverage: 0,
    });

    const config = reactive<RecommendConfig>({
      similarity_threshold: 0.0,
      max_related_games: 10,
      use_desc: true,
      use_tags: true,
      use_categories: true,
      desc_weight: 1.0,
      tags_weight: 1.5,
      categories_weight: 2.0,
      enable_diversity: true,
      diversity_threshold: 0.3,
      enable_business_rules: true,
    });

    const fetchStats = async () => {
      loading.value = true;
      try {
        const data = await RecommendApi.getStats();
        stats.value = data;
      } catch (error) {
        ElMessage.error(`加载统计信息失败：${error instanceof Error ? error.message : '未知错误'}`);
      } finally {
        loading.value = false;
      }
    };

    const refreshStats = () => {
      fetchStats();
    };

    const runCalculation = async () => {
      calculating.value = true;
      try {
        await RecommendApi.calculateRelations(config);
        ElMessage.success('相关性计算任务已启动，正在后台运行...');
        // 延迟刷新统计信息
        setTimeout(() => {
          fetchStats();
        }, 3000);
      } catch (error) {
        ElMessage.error(`启动计算任务失败：${error instanceof Error ? error.message : '未知错误'}`);
      } finally {
        calculating.value = false;
      }
    };

    onMounted(() => {
      fetchStats();
    });

    return {
      loading,
      calculating,
      stats,
      config,
      fetchStats,
      refreshStats,
      runCalculation,
    };
  },
});
</script>

<style scoped>
.recommend-container {
  padding: 20px;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.fixed-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.fixed-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.stats-section {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stats-content {
  display: flex;
  gap: 40px;
  padding: 20px 0;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stat-label {
  font-size: 14px;
  color: #666;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.config-section {
  flex: 1;
  overflow-y: auto;
}

.form-item-tip {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.is-loading {
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
